import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ViewNotes from "../../../../../../entities/DashboardPage/AccountingModule/Customer/Notes/ViewNotes";
import AuthService from "../../../../../../api/AuthService";
import LoaderHelper from "../../../../../../utils/Loader/LoaderHelper";
import { useAlert } from "../../../../../../context/AlertContext";
import ViewSalesOrderPage from "../../../../../../entities/DashboardPage/AccountingModule/Customer/SalesOrder/ViewSalesOrder";

const ViewSales = () => {
  const [loading, setLoading] = useState(false);
  const [editNoteData, setEditNoteData] = useState(null);
  const [salesOrderData, setSalesOrderData] = useState(null);
  const [itemList , setItemList] = useState([])
  const { showAlert } = useAlert();
  const { state } = useLocation();
  const { item } = state || {};
  const { _id } = item || {};

  useEffect(() => {
    handleShowPdf();
    getSalesOrder();
    getItemList()
  }, []);

  const handleShowPdf = async () => {
    LoaderHelper.loaderStatus(true);
    const result = await AuthService.getSalesOrderView(_id);
    LoaderHelper.loaderStatus(false);
    return { __html: result };
  };

  const handleDownloadReceipt = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.handleDownloadSalesOrderReceipt(_id);
      if (!result) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      } else {
        const blob = new Blob([result], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `FileName.${"pdf"}`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getEditNotesData = async (id) => {
    LoaderHelper.loaderStatus(true);
    setLoading(true)
    try {
      const result = await AuthService.getEditNotesData(id);
      setEditNoteData(result?.data);
      if (result?.success === true) {
        setLoading(false)
        LoaderHelper.loaderStatus(false);
      } else {
        setLoading(false)
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      setLoading(false)
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleSendSalesOrder = async () => {
    LoaderHelper.loaderStatus(true);
    setLoading(true)
    try {
      const result = await AuthService.sendSalesOrder(_id);
      if (result?.success === true) {
        setLoading(false)
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
        // Refresh data after successful send
        await refreshData();
      } else {
        setLoading(false)
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      setLoading(false)
      LoaderHelper.loaderStatus(false);
    }
  };

  const deleteCreditApply = async (id, creditAppliedID) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteApplyCredit(id, creditAppliedID);
      if (result?.success === true) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
        getEditNotesData(_id);
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const deleteRefundHistory = async (id, transactionId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.deleteRefundHistory(id, transactionId);
      if (result?.success === true) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
        getEditNotesData(_id);
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  }

  const handleFulfillmentComplete = async (id) => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.fulfillmentComplete(id);
      if (result?.success === true) {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "success");
        // Refresh data after successful completion
        await refreshData();
      } else {
        LoaderHelper.loaderStatus(false);
        showAlert(result?.message, "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const getSalesOrder = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getSalesOrder(_id);
      if (result?.success === true) {
        setSalesOrderData(result?.data);
        LoaderHelper.loaderStatus(false);
      } else {
        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  const updatePartialFullfillment = async (id, payload) => {
    try {
      LoaderHelper.loaderStatus(true);
      const result = await AuthService.updatePartialFullfillment(id, payload);
      LoaderHelper.loaderStatus(false);

      if (result?.success === true) {
        showAlert(result?.message, "success");
        // Refresh data after successful partial fulfillment
        await refreshData();
      } else {
        showAlert(result?.message || "Update failed", "error");
      }
    } catch (error) {
      LoaderHelper.loaderStatus(false);
      showAlert("Something went wrong", "error");
    }
  };

  const getItemList = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getItemsList();
      if (result?.success) {
        setItemList(result.data.items);
      }
      LoaderHelper.loaderStatus(false);
    } catch (error) {
      LoaderHelper.loaderStatus(false);
    }
  };

  // Comprehensive refresh function to reload all data
  const refreshData = async () => {
    try {
      await Promise.all([
        handleShowPdf(),
        getSalesOrder(),
        getItemList(),
        getEditNotesData(_id),
      ]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  return (
    <ViewSalesOrderPage
      handleDownloadReceipt={handleDownloadReceipt}
      handleShowPdf={handleShowPdf}
      getEditNotesData={getEditNotesData}
      editNoteData={editNoteData}
      loading={loading}
      handleSendSalesOrder={handleSendSalesOrder}
      deleteCreditApply={deleteCreditApply}
      deleteRefundHistory={deleteRefundHistory}
      handleFulfillmentComplete={handleFulfillmentComplete}
      salesOrderData={salesOrderData}
      updatePartialFullfillment={updatePartialFullfillment}
      itemList={itemList}
      refreshData={refreshData}
    />
  );
};

export default ViewSales;
